import styles from "./footer-v2.module.less";
import { TLink } from "../../../types";
import { isArrayWithItems } from "../../../helpers/array-helper";
import { LinkWrapper } from "../../elements";
import dayjs from "dayjs";

const FooterMenuItem = ({ title, internalLink, links }: TFooterMenuItem) => {
  return (
    <div>
      <LinkWrapper className={`${styles.columnTitle} body-base bold mb-sm`} title={title} url={internalLink?.url} />
      {isArrayWithItems(links) && (
        <ul className={styles.linkList}>
          {links.map((link) => {
            return (
              <li key={link.url}>
                <LinkWrapper className={"body-s"} enableExternalLinkIcon={true} url={link.url} title={link.title} />
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

type TFooterMenuItem = {
  title: string;
  internalLink?: TLink;
  links: Array<TLink>;
};

type FooterProps = {
  columnOne: TFooterMenuItem;
  columnTwo: TFooterMenuItem;
  columnThree: Array<TLink>;
  columnFour: TFooterMenuItem;
  columnFive: React.ReactNode;
  tagline: string;
  logoUrl: string;
  appStoreiOSLink?: TLink;
  appStoreAndroidLink?: TLink;
  addressLink?: TLink;
  phoneNumber?: string;
  importantLinks?: Array<TLink>;
};

const Footer = ({
  columnOne,
  columnTwo,
  columnThree,
  columnFour,
  columnFive,
  tagline,
  logoUrl,
  appStoreiOSLink,
  appStoreAndroidLink,
  addressLink,
  phoneNumber,
  importantLinks,
}: FooterProps) => {
  return (
    <footer className={styles.footer}>
      <div className={`theme-dark-blue ${styles.wrapper}`}>
        <div className={"centered max-width paddes-sides"}>
          <div className={`${styles.innerWrapper}`}>
            {columnOne && <FooterMenuItem {...columnOne} />}
            {columnTwo && <FooterMenuItem {...columnTwo} />}
            {columnThree && (
              <div>
                <ul className={styles.linkList}>
                  {columnThree.map((link) => {
                    return (
                      <li key={link.url}>
                        <LinkWrapper
                          className={`${styles.columnTitle} body-base bold mb-sm`}
                          title={link.title}
                          url={link?.url}
                        />
                      </li>
                    );
                  })}
                </ul>
              </div>
            )}
            {columnFour && <FooterMenuItem {...columnFour} />}
            {columnFive && <div className={styles.columnFive}>{columnFive}</div>}
          </div>
          <div className={`centered max-width paddes-sides ${styles.bottomArea}`}>
            <div className={styles.logoWrapper}>
              {logoUrl && <img className={styles.logo} src={logoUrl} alt={"Preem logo"} />}
              {tagline && <p className="h1 mb-0">{tagline}</p>}
            </div>
            <div className={styles.appLogos}>
              <a href={appStoreiOSLink?.url} title={appStoreiOSLink?.title} target={"_blank"} rel="noreferrer">
                <AppStoreSvg />
              </a>
              <a href={appStoreAndroidLink?.url} title={appStoreAndroidLink?.title} target={"_blank"} rel="noreferrer">
                <GooglePlaySvg />
              </a>
            </div>
          </div>
        </div>
      </div>
      <div className={`body-s mb-0 paddes-sides ${styles.additionalInfoWrapper}`}>
        <div className={`centered max-width ${styles.additionalInfo}`}>
          <div className={styles.contactWrapper}>
            <p>{`Copyright © ${dayjs().format("YYYY")} Preem AB`}</p>
            {addressLink && (
              <LinkWrapper url={addressLink.url} title={addressLink.title} enableExternalLinkIcon={true} />
            )}
            {phoneNumber && (
              <p>
                Växel: <a href={`tel:${phoneNumber}`}>{phoneNumber}</a>
              </p>
            )}
          </div>
          {isArrayWithItems(importantLinks) && (
            <ul className={styles.importantLinks}>
              {importantLinks.map((link) => {
                return (
                  <li key={link.url}>
                    <LinkWrapper url={link.url} title={link.title} enableExternalLinkIcon={true} />
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>
    </footer>
  );
};

export default Footer;

const AppStoreSvg = () => {
  return (
    <svg width="120" height="40" viewBox="0 0 120 40" fill="none" xmlns="http://www.w3.org/2000/svg">
      <g clipPath="url(#clip0_12378_2427)">
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M110.135 0.00013H9.53468C9.16798 0.00013 8.80568 0.00013 8.43995 0.00213C8.1338 0.00413 7.83009 0.00994 7.521 0.01483C6.84951 0.0227318 6.17961 0.0818063 5.5171 0.19154C4.85552 0.303663 4.21467 0.515046 3.61622 0.81854C3.0185 1.1246 2.47235 1.5223 1.99757 1.9972C1.5203 2.47077 1.12246 3.01815 0.81935 3.61829C0.5154 4.21724 0.304641 4.85907 0.19435 5.52161C0.0830109 6.18332 0.0230984 6.85265 0.01515 7.52361C0.00587 7.83021 0.00489 8.13783 0 8.44447V31.5587C0.00489 31.8692 0.00587 32.17 0.01515 32.4806C0.0231008 33.1516 0.0830134 33.8209 0.19435 34.4825C0.304336 35.1455 0.515108 35.7877 0.81935 36.3868C1.12233 36.985 1.52022 37.5302 1.99757 38.0011C2.47054 38.4781 3.01705 38.8761 3.61622 39.1798C4.21467 39.4841 4.85545 39.6968 5.5171 39.8106C6.17972 39.9195 6.84956 39.9786 7.521 39.9874C7.83009 39.9942 8.1338 39.9981 8.43995 39.9981C8.80567 40.0001 9.168 40.0001 9.53468 40.0001H110.135C110.494 40.0001 110.859 40.0001 111.219 39.9981C111.523 39.9981 111.836 39.9942 112.141 39.9874C112.811 39.9791 113.479 39.92 114.141 39.8106C114.804 39.696 115.448 39.4834 116.049 39.1798C116.647 38.8759 117.193 38.478 117.666 38.0011C118.142 37.5284 118.541 36.9836 118.848 36.3868C119.15 35.7872 119.358 35.1451 119.467 34.4825C119.578 33.8208 119.64 33.1516 119.652 32.4806C119.656 32.17 119.656 31.8692 119.656 31.5587C119.664 31.1954 119.664 30.8341 119.664 30.4649V9.53626C119.664 9.17005 119.664 8.80677 119.656 8.44447C119.656 8.13783 119.656 7.83021 119.652 7.52357C119.64 6.85255 119.578 6.18337 119.467 5.52157C119.358 4.85941 119.149 4.21763 118.848 3.61825C118.23 2.41533 117.252 1.43616 116.049 0.81845C115.448 0.515697 114.804 0.30437 114.141 0.19145C113.48 0.0812328 112.811 0.0221378 112.141 0.01469C111.836 0.00981 111.523 0.00395 111.219 0.002C110.859 0 110.494 0.00013 110.135 0.00013Z"
          fill="#A6A6A6"
        />
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M8.44483 39.125C8.14015 39.125 7.84283 39.1211 7.54054 39.1143C6.91432 39.1061 6.28957 39.0516 5.6714 38.9512C5.09499 38.8519 4.5366 38.6673 4.01466 38.4033C3.4975 38.1415 3.02581 37.7983 2.61766 37.3867C2.2036 36.98 1.85888 36.5082 1.59715 35.9902C1.33254 35.4688 1.14941 34.9099 1.05415 34.333C0.951273 33.7131 0.895613 33.0863 0.887649 32.458C0.881309 32.2471 0.873009 31.5449 0.873009 31.5449V8.44434C0.873009 8.44434 0.881849 7.75293 0.887699 7.5498C0.895325 6.92248 0.950661 6.29665 1.05323 5.67773C1.14867 5.09925 1.33194 4.53875 1.59669 4.01563C1.85745 3.49794 2.20026 3.02586 2.61183 2.61768C3.02293 2.20562 3.49613 1.8606 4.01417 1.59521C4.53491 1.33209 5.09224 1.14873 5.66749 1.05127C6.28769 0.949836 6.91461 0.894996 7.543 0.88721L8.44532 0.875H111.214L112.127 0.8877C112.75 0.895099 113.371 0.94945 113.985 1.05029C114.566 1.14898 115.13 1.33362 115.656 1.59814C116.694 2.13299 117.539 2.97916 118.071 4.01807C118.332 4.53758 118.512 5.09351 118.606 5.66699C118.71 6.29099 118.768 6.92174 118.78 7.5542C118.783 7.8374 118.783 8.1416 118.783 8.44434C118.791 8.81934 118.791 9.17627 118.791 9.53613V30.4648C118.791 30.8281 118.791 31.1826 118.783 31.54C118.783 31.8652 118.783 32.1631 118.779 32.4697C118.768 33.0909 118.71 33.7104 118.608 34.3232C118.515 34.9043 118.333 35.4675 118.068 35.9932C117.805 36.5056 117.462 36.9733 117.053 37.3789C116.644 37.7927 116.172 38.1379 115.653 38.4014C115.128 38.6674 114.566 38.8527 113.985 38.9512C113.367 39.0522 112.742 39.1067 112.116 39.1143C111.823 39.1211 111.517 39.125 111.219 39.125L110.135 39.127L8.44483 39.125Z"
          fill="black"
        />
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M24.7689 20.3006C24.7907 18.6042 25.6801 17.0371 27.1254 16.1486C26.2095 14.8405 24.7304 14.0408 23.1343 13.9909C21.455 13.8146 19.8271 14.9957 18.9714 14.9957C18.0991 14.9957 16.7816 14.0084 15.3629 14.0376C13.4962 14.0979 11.7981 15.1335 10.89 16.7654C8.95596 20.1139 10.3985 25.0349 12.2512 27.7415C13.1781 29.0669 14.2613 30.5473 15.6788 30.4948C17.0658 30.4373 17.5839 29.6103 19.2582 29.6103C20.9169 29.6103 21.403 30.4948 22.8492 30.4614C24.3376 30.4373 25.2753 29.1302 26.1697 27.7923C26.8357 26.8479 27.3481 25.8042 27.6881 24.6998C25.9204 23.9521 24.7709 22.22 24.7689 20.3006ZM22.0372 12.2108C22.8488 11.2366 23.2486 9.98445 23.1518 8.72021C21.9119 8.85044 20.7667 9.44299 19.9442 10.3798C19.132 11.3041 18.7206 12.5134 18.8005 13.7412C20.0569 13.7542 21.2499 13.1901 22.0372 12.2108Z"
          fill="white"
        />
        <path
          d="M42.3023 27.1397H37.5689L36.4322 30.4961H34.4273L38.9107 18.0781H40.9937L45.4771 30.4961H43.438L42.3023 27.1397ZM38.0591 25.5908H41.8111L39.9615 20.1436H39.9097L38.0591 25.5908ZM55.1597 25.9698C55.1597 28.7832 53.6538 30.5908 51.3814 30.5908C50.2068 30.6523 49.1003 30.0371 48.5327 29.0068H48.4897V33.4912H46.6313V21.4424H48.4302V22.9483H48.4644C49.0572 21.9245 50.1648 21.3095 51.3472 21.3477C53.645 21.3477 55.1597 23.1641 55.1597 25.9698ZM53.2495 25.9698C53.2495 24.1368 52.3023 22.9317 50.857 22.9317C49.437 22.9317 48.482 24.1621 48.482 25.9698C48.482 27.794 49.437 29.0157 50.857 29.0157C52.3023 29.0157 53.2495 27.8194 53.2495 25.9698ZM65.1245 25.9698C65.1245 28.7832 63.6187 30.5908 61.3462 30.5908C60.1716 30.6523 59.0652 30.0371 58.4976 29.0068H58.4546V33.4912H56.5962V21.4424H58.395V22.9483H58.4292C59.022 21.9245 60.1296 21.3095 61.312 21.3477C63.6099 21.3477 65.1245 23.1641 65.1245 25.9698ZM63.2144 25.9698C63.2144 24.1368 62.2671 22.9317 60.8218 22.9317C59.4019 22.9317 58.4468 24.1621 58.4468 25.9698C58.4468 27.794 59.4019 29.0157 60.8218 29.0157C62.2671 29.0157 63.2144 27.8194 63.2144 25.9698Z"
          fill="white"
        />
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M71.7105 27.0362C71.8482 28.2676 73.0445 29.0762 74.6792 29.0762C76.2456 29.0762 77.3726 28.2676 77.3726 27.1572C77.3726 26.1934 76.6929 25.6162 75.0835 25.2207L73.4742 24.833C71.1939 24.2822 70.1353 23.2158 70.1353 21.4854C70.1353 19.3428 72.0025 17.8711 74.6538 17.8711C77.2778 17.8711 79.0767 19.3428 79.1372 21.4854H77.2612C77.1489 20.2461 76.1245 19.4981 74.6274 19.4981C73.1304 19.4981 72.106 20.2549 72.106 21.3565C72.106 22.2344 72.7603 22.751 74.3608 23.1465L75.729 23.4824C78.2769 24.0849 79.3355 25.1084 79.3355 26.9248C79.3355 29.248 77.4849 30.7031 74.5415 30.7031C71.7876 30.7031 69.9282 29.2822 69.8081 27.0361L71.7105 27.0362ZM83.3462 19.2999V21.4424H85.0679V22.9141H83.3462V27.9053C83.3462 28.6807 83.6909 29.042 84.4478 29.042C84.6522 29.0385 84.8562 29.0241 85.0591 28.999V30.4619C84.7188 30.5255 84.373 30.5543 84.0269 30.5479C82.1939 30.5479 81.479 29.8594 81.479 28.1035V22.9141H80.1626V21.4424H81.479V19.2999H83.3462Z"
          fill="white"
        />
        <path
          d="M86.065 25.9697C86.065 23.1211 87.7427 21.3311 90.359 21.3311C92.984 21.3311 94.6539 23.1211 94.6539 25.9697C94.6539 28.8262 92.9927 30.6084 90.359 30.6084C87.7261 30.6084 86.065 28.8262 86.065 25.9697ZM92.7603 25.9697C92.7603 24.0156 91.8648 22.8623 90.3589 22.8623C88.8531 22.8623 87.9586 24.0244 87.9586 25.9697C87.9586 27.9316 88.8531 29.0762 90.3589 29.0762C91.8648 29.0762 92.7603 27.9316 92.7603 25.9697Z"
          fill="white"
        />
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M96.1861 21.4425H97.9585V22.9835H98.0015C98.2487 21.9915 99.1577 21.3088 100.179 21.3478C100.393 21.347 100.607 21.3703 100.816 21.4171V23.1554C100.546 23.0728 100.264 23.0349 99.981 23.0431C99.4346 23.0209 98.9059 23.2387 98.5336 23.6391C98.1613 24.0396 97.9826 24.5828 98.0445 25.1261V30.4962H96.1861L96.1861 21.4425Z"
          fill="white"
        />
        <path
          d="M109.384 27.8369C109.134 29.4805 107.534 30.6084 105.486 30.6084C102.852 30.6084 101.217 28.8437 101.217 26.0127C101.217 23.1729 102.861 21.3311 105.408 21.3311C107.913 21.3311 109.488 23.0518 109.488 25.7969V26.4336H103.093V26.5459C103.034 27.2199 103.266 27.8871 103.732 28.3778C104.198 28.8684 104.853 29.1352 105.529 29.1103C106.431 29.1948 107.281 28.677 107.62 27.8369L109.384 27.8369ZM103.102 25.1348L107.628 25.1348C107.662 24.5291 107.442 23.9368 107.02 23.5006C106.599 23.0644 106.014 22.8239 105.408 22.8369C104.796 22.8333 104.209 23.0739 103.776 23.5054C103.343 23.9369 103.101 24.5235 103.102 25.1348Z"
          fill="white"
        />
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M39.662 14.698V12.08H36.598V14.698H35.671V8.73096H36.598V11.237H39.662V8.73096H40.584V14.698H39.662Z"
          fill="white"
        />
        <path
          d="M41.8467 13.4242C41.8467 12.6137 42.4502 12.1464 43.5215 12.08L44.7412 12.0097V11.621C44.7412 11.1454 44.4268 10.8769 43.8194 10.8769C43.3233 10.8769 42.9795 11.059 42.8809 11.3774H42.0205C42.1114 10.6039 42.8389 10.1078 43.8604 10.1078C44.9893 10.1078 45.626 10.6698 45.626 11.621V14.6977H44.7705V14.0649H44.7002C44.4097 14.527 43.893 14.7971 43.3477 14.7719C42.9654 14.8116 42.5842 14.6878 42.2982 14.4311C42.0122 14.1743 41.8482 13.8086 41.8467 13.4242ZM42.4258 9.02434C42.4272 8.72019 42.6744 8.47449 42.9785 8.47503C43.2827 8.47556 43.5291 8.72214 43.5293 9.0263C43.5296 9.33045 43.2837 9.57746 42.9795 9.57854C42.832 9.58045 42.69 9.52266 42.5857 9.4183C42.4814 9.31393 42.4238 9.17186 42.4258 9.02434ZM44.7412 13.0395V12.663L43.6416 12.7333C43.0215 12.7748 42.7403 12.9858 42.7403 13.3827C42.7403 13.788 43.0918 14.0239 43.5753 14.0239C43.8614 14.0528 44.1471 13.9644 44.3669 13.7788C44.5867 13.5933 44.7218 13.3265 44.7412 13.0395ZM44.1914 9.02434C44.1894 8.82584 44.2941 8.64153 44.4656 8.54168C44.6372 8.44183 44.8492 8.44183 45.0208 8.54168C45.1923 8.64153 45.297 8.82584 45.295 9.02434C45.295 9.32907 45.0479 9.5761 44.7432 9.5761C44.4385 9.5761 44.1914 9.32907 44.1914 9.02434Z"
          fill="white"
        />
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M47.0176 10.1948H47.8731V10.9185H47.9395C48.1582 10.4256 48.6468 10.1079 49.1861 10.1079C49.7253 10.1079 50.2139 10.4256 50.4326 10.9185H50.503C50.7597 10.4021 51.2959 10.0847 51.8721 10.1079C52.2717 10.0752 52.6649 10.2233 52.9435 10.5116C53.2222 10.7999 53.3569 11.1979 53.3106 11.5962V14.6977H52.4219V11.8281C52.4219 11.2202 52.1319 10.9224 51.5489 10.9224C51.295 10.9115 51.0481 11.0071 50.8677 11.1861C50.6873 11.3651 50.5898 11.6113 50.5987 11.8652V14.6978H49.7257V11.7412C49.737 11.5119 49.6474 11.2892 49.4804 11.1317C49.3134 10.9741 49.0858 10.8977 48.8575 10.9224C48.5943 10.9267 48.3441 11.038 48.1646 11.2307C47.9852 11.4234 47.892 11.6809 47.9064 11.9438V14.6978L47.0176 14.6978V10.1948ZM55.8877 9.07373V10.2153H56.8633V10.9639H55.8877V13.2793C55.8877 13.751 56.0821 13.9575 56.5244 13.9575C56.6377 13.9572 56.7508 13.9503 56.8633 13.937V14.6772C56.7037 14.7058 56.542 14.721 56.3799 14.7227C55.3916 14.7227 54.9981 14.375 54.9981 13.5068V10.9638H54.2832V10.2153H54.9981V9.07373H55.8877Z"
          fill="white"
        />
        <path
          d="M57.7793 13.4244C57.7793 12.6139 58.3828 12.1466 59.4541 12.0802L60.6739 12.0099V11.6212C60.6739 11.1456 60.3594 10.8771 59.752 10.8771C59.2559 10.8771 58.9121 11.0592 58.8135 11.3775H57.9532C58.044 10.6041 58.7715 10.108 59.793 10.108C60.9219 10.108 61.5586 10.67 61.5586 11.6212V14.6979H60.7032V14.065H60.6328C60.3423 14.5272 59.8256 14.7973 59.2803 14.772C58.898 14.8118 58.5168 14.688 58.2308 14.4312C57.9448 14.1745 57.7808 13.8087 57.7793 13.4244ZM60.6739 13.0396V12.6632L59.5742 12.7335C58.9541 12.775 58.6729 12.9859 58.6729 13.3829C58.6729 13.7882 59.0244 14.024 59.5079 14.024C59.7941 14.053 60.0798 13.9646 60.2995 13.779C60.5193 13.5934 60.6544 13.3266 60.6739 13.0396ZM65.2769 8.86337C65.2629 8.62786 65.394 8.40762 65.6077 8.30766C65.8214 8.2077 66.0744 8.24824 66.2462 8.40996C66.418 8.57168 66.4737 8.82184 66.3868 9.04119C66.2999 9.26054 66.088 9.40467 65.8521 9.40487C65.7032 9.41246 65.5576 9.35897 65.449 9.25676C65.3405 9.15455 65.2783 9.01248 65.2769 8.86337ZM65.4087 10.1949H66.2945V14.6978H65.4087V10.1949Z"
          fill="white"
        />
      </g>
      <defs>
        <clipPath id="clip0_12378_2427">
          <rect width="120" height="40" fill="white" />
        </clipPath>
      </defs>
    </svg>
  );
};

const GooglePlaySvg = () => {
  return (
    <svg width="135" height="40" viewBox="0 0 135 40" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M130 40H5C2.2 40 0 37.8 0 35V5C0 2.2 2.2 0 5 0H130C132.8 0 135 2.2 135 5V35C135 37.7 132.8 40 130 40Z"
        fill="black"
      />
      <path
        d="M130 0.8C132.3 0.8 134.2 2.7 134.2 5V35C134.2 37.3 132.3 39.2 130 39.2H5C2.7 39.2 0.8 37.3 0.8 35V5C0.8 2.7 2.7 0.8 5 0.8H130ZM130 0H5C2.2 0 0 2.2 0 5V35C0 37.8 2.2 40 5 40H130C132.8 40 135 37.8 135 35V5C135 2.3 132.8 0 130 0Z"
        fill="#A6A6A6"
      />
      <path
        d="M68.1 21.8001C65.7 21.8001 63.8 23.6001 63.8 26.1001C63.8 28.5001 65.7 30.4001 68.1 30.4001C70.5 30.4001 72.4 28.6001 72.4 26.1001C72.4 23.5001 70.5 21.8001 68.1 21.8001ZM68.1 28.6001C66.8 28.6001 65.7 27.5001 65.7 26.0001C65.7 24.5001 66.8 23.4001 68.1 23.4001C69.4 23.4001 70.5 24.4001 70.5 26.0001C70.5 27.5001 69.4 28.6001 68.1 28.6001ZM58.8 21.8001C56.4 21.8001 54.5 23.6001 54.5 26.1001C54.5 28.5001 56.4 30.4001 58.8 30.4001C61.2 30.4001 63.1 28.6001 63.1 26.1001C63.1 23.5001 61.2 21.8001 58.8 21.8001ZM58.8 28.6001C57.5 28.6001 56.4 27.5001 56.4 26.0001C56.4 24.5001 57.5 23.4001 58.8 23.4001C60.1 23.4001 61.2 24.4001 61.2 26.0001C61.2 27.5001 60.1 28.6001 58.8 28.6001ZM47.7 23.1001V24.9001H52C51.9 25.9001 51.5 26.7001 51 27.2001C50.4 27.8001 49.4 28.5001 47.7 28.5001C45 28.5001 43 26.4001 43 23.7001C43 21.0001 45.1 18.9001 47.7 18.9001C49.1 18.9001 50.2 19.5001 51 20.2001L52.3 18.9001C51.2 17.9001 49.8 17.1001 47.8 17.1001C44.2 17.1001 41.1 20.1001 41.1 23.7001C41.1 27.3001 44.2 30.3001 47.8 30.3001C49.8 30.3001 51.2 29.7001 52.4 28.4001C53.6 27.2001 54 25.5001 54 24.2001C54 23.8001 54 23.4001 53.9 23.1001H47.7ZM93.1 24.5001C92.7 23.5001 91.7 21.8001 89.5 21.8001C87.3 21.8001 85.5 23.5001 85.5 26.1001C85.5 28.5001 87.3 30.4001 89.7 30.4001C91.6 30.4001 92.8 29.2001 93.2 28.5001L91.8 27.5001C91.3 28.2001 90.7 28.7001 89.7 28.7001C88.7 28.7001 88.1 28.3001 87.6 27.4001L93.3 25.0001L93.1 24.5001ZM87.3 25.9001C87.3 24.3001 88.6 23.4001 89.5 23.4001C90.2 23.4001 90.9 23.8001 91.1 24.3001L87.3 25.9001ZM82.6 30.0001H84.5V17.5001H82.6V30.0001ZM79.6 22.7001C79.1 22.2001 78.3 21.7001 77.3 21.7001C75.2 21.7001 73.2 23.6001 73.2 26.0001C73.2 28.4001 75.1 30.2001 77.3 30.2001C78.3 30.2001 79.1 29.7001 79.5 29.2001H79.6V29.8001C79.6 31.4001 78.7 32.3001 77.3 32.3001C76.2 32.3001 75.4 31.5001 75.2 30.8001L73.6 31.5001C74.1 32.6001 75.3 34.0001 77.4 34.0001C79.6 34.0001 81.4 32.7001 81.4 29.6001V22.0001H79.6V22.7001ZM77.4 28.6001C76.1 28.6001 75 27.5001 75 26.0001C75 24.5001 76.1 23.4001 77.4 23.4001C78.7 23.4001 79.7 24.5001 79.7 26.0001C79.7 27.5001 78.7 28.6001 77.4 28.6001ZM101.8 17.5001H97.3V30.0001H99.2V25.3001H101.8C103.9 25.3001 105.9 23.8001 105.9 21.4001C105.9 19.0001 103.9 17.5001 101.8 17.5001ZM101.9 23.5001H99.2V19.2001H101.9C103.3 19.2001 104.1 20.4001 104.1 21.3001C104 22.4001 103.2 23.5001 101.9 23.5001ZM113.4 21.7001C112 21.7001 110.6 22.3001 110.1 23.6001L111.8 24.3001C112.2 23.6001 112.8 23.4001 113.5 23.4001C114.5 23.4001 115.4 24.0001 115.5 25.0001V25.1001C115.2 24.9001 114.4 24.6001 113.6 24.6001C111.8 24.6001 110 25.6001 110 27.4001C110 29.1001 111.5 30.2001 113.1 30.2001C114.4 30.2001 115 29.6001 115.5 29.0001H115.6V30.0001H117.4V25.2001C117.2 23.0001 115.5 21.7001 113.4 21.7001ZM113.2 28.6001C112.6 28.6001 111.7 28.3001 111.7 27.5001C111.7 26.5001 112.8 26.2001 113.7 26.2001C114.5 26.2001 114.9 26.4001 115.4 26.6001C115.2 27.8001 114.2 28.6001 113.2 28.6001ZM123.7 22.0001L121.6 27.4001H121.5L119.3 22.0001H117.3L120.6 29.6001L118.7 33.8001H120.6L125.7 22.0001H123.7ZM106.9 30.0001H108.8V17.5001H106.9V30.0001Z"
        fill="white"
      />
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M10.4 7.5C10.1 7.8 9.89999 8.3 9.89999 8.9V31C9.89999 31.6 10.1 32.1 10.4 32.4L10.5 32.5L22.9 20.1V20V19.9L10.4 7.5Z"
        fill="url(#paint0_linear_12378_2445)"
      />
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M27 24.2998L22.9 20.1998V19.9998V19.8998L27 15.7998L27.1 15.8998L32 18.6998C33.4 19.4998 33.4 20.7998 32 21.5998L27 24.2998Z"
        fill="url(#paint1_linear_12378_2445)"
      />
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M27.1 24.2L22.9 20L10.4 32.5C10.9 33 11.6 33 12.5 32.6L27.1 24.2Z"
        fill="url(#paint2_linear_12378_2445)"
      />
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M27.1 15.8001L12.5 7.50011C11.6 7.00011 10.9 7.10011 10.4 7.60011L22.9 20.0001L27.1 15.8001Z"
        fill="url(#paint3_linear_12378_2445)"
      />
      <path
        opacity="0.2"
        fillRule="evenodd"
        clipRule="evenodd"
        d="M27 24.1001L12.5 32.3001C11.7 32.8001 11 32.7001 10.5 32.3001L10.4 32.4001L10.5 32.5001C11 32.9001 11.7 33.0001 12.5 32.5001L27 24.1001Z"
        fill="black"
      />
      <path
        opacity="0.12"
        fillRule="evenodd"
        clipRule="evenodd"
        d="M10.4 32.3C10.1 32 10 31.5 10 30.9V31C10 31.6 10.2 32.1 10.5 32.4V32.3H10.4ZM32 21.3L27 24.1L27.1 24.2L32 21.4C32.7 21 33 20.5 33 20C33 20.5 32.6 20.9 32 21.3Z"
        fill="black"
      />
      <path
        opacity="0.25"
        fillRule="evenodd"
        clipRule="evenodd"
        d="M12.5 7.59988L32 18.6999C32.6 19.0999 33 19.4999 33 19.9999C33 19.4999 32.7 18.9999 32 18.5999L12.5 7.49988C11.1 6.69988 10 7.39988 10 8.99988V9.09988C10 7.49988 11.1 6.79988 12.5 7.59988Z"
        fill="white"
      />
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M41.2 13V7H42V12.3H44.6V13H41.2Z"
        fill="white"
        stroke="white"
        strokeWidth="0.2"
      />
      <path
        d="M45.1 13L47.4 7H48.3L50.6 13H49.7L49 11.4H46.5L45.9 13H45.1ZM46.8 10.7H48.8L47.8 8L46.8 10.7ZM51.3 13V7H53.1C54 7 54.8 7.3 55.3 7.8C55.9 8.4 56.1 9.1 56.1 10C56.1 10.9 55.8 11.6 55.3 12.2C54.7 12.8 54 13 53.1 13H51.3ZM52.1 12.3H53.2C53.9 12.3 54.4 12.1 54.8 11.7C55.2 11.3 55.4 10.7 55.4 10C55.4 9.3 55.2 8.7 54.8 8.3C54.4 7.9 53.8 7.7 53.2 7.7H52.1V12.3ZM57.3 13V7H59.1C60 7 60.8 7.3 61.3 7.8C61.9 8.4 62.1 9.1 62.1 10C62.1 10.9 61.8 11.6 61.3 12.2C60.8 12.7 60 13 59.1 13H57.3ZM58.1 12.3H59.2C59.9 12.3 60.4 12.1 60.8 11.7C61.2 11.3 61.4 10.7 61.4 10C61.4 9.3 61.2 8.7 60.8 8.3C60.4 7.9 59.8 7.7 59.2 7.7H58.1V12.3ZM62.5 13L64.8 7H65.7L68 13H67L66.4 11.4H63.9L63.3 13H62.5ZM64.2 10.7H66.2L65.2 8L64.2 10.7Z"
        fill="white"
        stroke="white"
        strokeWidth="0.2"
      />
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M70.8 13V7H71.7L74.6 11.7V10.5V7H75.4V13H74.6L71.5 8.1V9.3V13H70.8Z"
        fill="white"
        stroke="white"
        strokeWidth="0.2"
      />
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M80.3 7.7H77.6V9.6H80V10.3H77.5V12.2H80.2V13H76.7V7H80.2V7.7H80.3Z"
        fill="white"
        stroke="white"
        strokeWidth="0.2"
      />
      <path
        d="M81.5 13.0002V7.0002H83.3C84.2 7.0002 85 7.3002 85.5 7.8002C86.1 8.4002 86.3 9.1002 86.3 10.0002C86.3 10.9002 86 11.6002 85.5 12.2002C85 12.7002 84.2 13.0002 83.3 13.0002H81.5ZM82.2 12.3002H83.3C84 12.3002 84.5 12.1002 84.9 11.7002C85.3 11.3002 85.5 10.7002 85.5 10.0002C85.5 9.3002 85.3 8.7002 84.9 8.3002C84.5 7.9002 83.9 7.7002 83.3 7.7002H82.2V12.3002ZM90.2 13.0002H89.4V7.0002H91.4C91.9 7.0002 92.3 7.2002 92.7 7.5002C93.1 7.8002 93.3 8.3002 93.3 8.8002C93.3 9.3002 93.1 9.7002 92.7 10.1002C92.3 10.4002 91.9 10.6002 91.4 10.6002H90.1V13.0002H90.2ZM90.2 9.8002H91.5C91.8 9.8002 92.1 9.7002 92.3 9.5002C92.5 9.3002 92.6 9.0002 92.6 8.8002C92.6 8.5002 92.5 8.3002 92.3 8.1002C92.1 7.9002 91.8 7.8002 91.5 7.8002H90.2V9.8002ZM93.2 13.0002L95.3 7.3002C95.1 7.1002 95 6.9002 95 6.6002C95 6.4002 95.1 6.1002 95.3 6.0002C95.5 5.8002 95.7 5.7002 95.9 5.7002C96.1 5.7002 96.3 5.8002 96.5 6.0002C96.7 6.2002 96.8 6.4002 96.8 6.6002C96.8 6.9002 96.7 7.1002 96.5 7.3002L98.6 13.0002H97.7L97.1 11.4002H94.6L94 13.0002H93.2ZM95 10.7002H97L96 8.0002L95 10.7002ZM95.9 7.0002C96 7.0002 96.1 7.0002 96.1 6.9002C96.2 6.8002 96.2 6.8002 96.2 6.7002C96.2 6.6002 96.2 6.5002 96.1 6.5002C96 6.4002 96 6.4002 95.9 6.4002C95.8 6.4002 95.7 6.4002 95.7 6.5002C95.6 6.6002 95.6 6.6002 95.6 6.7002C95.6 6.8002 95.6 6.9002 95.7 6.9002C95.8 7.0002 95.8 7.0002 95.9 7.0002Z"
        fill="white"
        stroke="white"
        strokeWidth="0.2"
      />
      <defs>
        <linearGradient
          id="paint0_linear_12378_2445"
          x1="1199.87"
          y1="128.475"
          x2="-786.894"
          y2="665.692"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#00A0FF" />
          <stop offset="0.00657" stopColor="#00A1FF" />
          <stop offset="0.2601" stopColor="#00BEFF" />
          <stop offset="0.5122" stopColor="#00D2FF" />
          <stop offset="0.7604" stopColor="#00DFFF" />
          <stop offset="1" stopColor="#00E3FF" />
        </linearGradient>
        <linearGradient
          id="paint1_linear_12378_2445"
          x1="1116.34"
          y1="435.938"
          x2="-1303.35"
          y2="435.938"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#FFE000" />
          <stop offset="0.4087" stopColor="#FFBD00" />
          <stop offset="0.7754" stopColor="#FFA500" />
          <stop offset="1" stopColor="#FF9C00" />
        </linearGradient>
        <linearGradient
          id="paint2_linear_12378_2445"
          x1="1453.1"
          y1="249.609"
          x2="-143.44"
          y2="2930.05"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#FF3A44" />
          <stop offset="1" stopColor="#C31162" />
        </linearGradient>
        <linearGradient
          id="paint3_linear_12378_2445"
          x1="-299.869"
          y1="-692.341"
          x2="407.431"
          y2="506.236"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#32A071" />
          <stop offset="0.0685" stopColor="#2DA771" />
          <stop offset="0.4762" stopColor="#15CF74" />
          <stop offset="0.8009" stopColor="#06E775" />
          <stop offset="1" stopColor="#00F076" />
        </linearGradient>
      </defs>
    </svg>
  );
};
